import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { CommonModule } from "@angular/common";
import { MatExpansionModule } from "@angular/material/expansion";
import { IImportError, IImportResult } from "../../models/import-result.interface";

@Component({
    selector: 'import-result-dialog',
    standalone: true,
    templateUrl: './import-result.dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatExpansionModule
    ]
})
export class ImportResultDialog {
    private readonly _dialogRef = inject(MatDialogRef<ImportResultDialog>);
    private readonly _data = inject<IImportResult>(MAT_DIALOG_DATA);

    readonly result = signal<IImportResult>(this._data);
    
    readonly hasErrors = computed(() => this.result().errorCount > 0);
    readonly hasSuccesses = computed(() => this.result().successCount > 0);
    readonly isPartialSuccess = computed(() => this.hasSuccesses() && this.hasErrors());
    readonly isFullSuccess = computed(() => this.result().successCount === this.result().totalCount && !this.hasErrors());
    readonly isFullFailure = computed(() => this.result().errorCount === this.result().totalCount && !this.hasSuccesses());

    // Agrupar erros por tipo
    readonly groupedErrors = computed(() => {
        const errors = this.result().errors;
        const grouped = new Map<string, IImportError[]>();
        
        errors.forEach(error => {
            const errorType = this.extractErrorType(error.error);
            if (!grouped.has(errorType)) {
                grouped.set(errorType, []);
            }
            grouped.get(errorType)!.push(error);
        });
        
        return Array.from(grouped.entries()).map(([type, errorList]) => ({
            type,
            errors: errorList,
            count: errorList.length
        }));
    });

    private extractErrorType(errorMessage: string): string {
        if (errorMessage.includes('já existe')) {
            return 'Usuário já existe';
        }
        if (errorMessage.includes('webhook')) {
            return 'Erro de webhook';
        }
        if (errorMessage.includes('não encontrado')) {
            return 'Cliente não encontrado';
        }
        return 'Outros erros';
    }

    downloadErrorReport() {
        const errors = this.result().errors;
        const csvContent = this.generateErrorCsv(errors);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `relatorio-erros-importacao-${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private generateErrorCsv(errors: IImportError[]): string {
        const header = 'Linha,Email,Erro';
        const rows = errors.map(error => 
            `${error.rowNumber},"${error.email}","${error.error.replace(/"/g, '""')}"`
        );
        return [header, ...rows].join('\n');
    }

    onCloseClick() {
        this._dialogRef.close(true);
    }
}

